<template>
  <v-layout v-if="!$apollo.loading" column class="wrapper">
    <v-flex class="title-screen" xs12>
      <span>{{ adminText('workflowsAll.floor_plan_overview_36d5a0f', 'Floor plan overview') }}</span>
    </v-flex>
    <v-select
      v-if="eventTemplateId"
      v-model="selectedEventId"
      :items="workflowUi(performanceDates)"
      :label="adminText('workflowsAll.performance_63c9045', 'Performance')"
      item-text="from"
      item-value="id"
      box
      style="max-width: 1000px"
    />
    <spinner v-if="!showFloorPlan" />
    <div v-else-if="!venue">{{ adminText('workflowsAll.this_event_does_not_have_a_floor_plan_a553e18', 'This event does not have a floor plan.') }}</div>
    <div v-else class="floor-plan-wrapper">
      <div class="order-items-container">
        <v-autocomplete
          v-model="selectedOrderItemIds"
          class="order-items-dropdown"
          :items="workflowUi(orderItemSelectItems)"
          item-text="label"
          item-value="id"
          :label="adminText('workflowsAll.select_tickets_to_highlight_6677788', 'Select tickets to highlight')"
          name="ticket-highlight-filter"
          browser-autocomplete="off"
          autocomplete="off"
          multiple
          chips
          small-chips
          clearable
          hide-details
          outlined
        >
          <template #selection="{ item, index }">
            <v-chip
              :key="`selected-${index}-${resolveOrderItemId(item)}`"
              small
              class="order-item-select-chip"
              @mousedown.stop.prevent
            >
              <span class="color order-item-select-color" :style="{ backgroundColor: item.color }" />
              <span class="order-item-select-label">{{ workflowLabel(item.label) }}</span>
              <button
                type="button"
                class="order-item-select-remove"
                :aria-label="adminText('workflowsAll.remove_selected_ticket_330efef', 'Remove selected ticket')"
                @mousedown.stop.prevent
                @click.stop.prevent="removeSelectedOrderItemBySelection(item)"
              >
                <v-icon size="14">close</v-icon>
              </button>
            </v-chip>
          </template>
          <template #item="{ item, attrs, on }">
            <v-list-item v-bind="attrs" v-on="on">
              <v-list-item-content>
                <div class="order-item-select-option">
                  <span class="color" :style="{ backgroundColor: item.color }" />
                  <span>{{ workflowLabel(item.label) }}</span>
                </div>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-autocomplete>
      </div>
      <div class="floor-plan-container">
        <div class="object-container">
          <spinner v-if="coloringInProgress" />
          <object
            v-show="!coloringInProgress"
            id="svgfile"
            :data="workflowUi(venue.imageUrl)"
            type="image/svg+xml"
            :style="floorPlanSvgStyle"
            @load="onSvgLoaded"
          />
        </div>
        <div class="total-label">
          <span class="base-text">{{ adminText('workflowsAll.free_75f5271', 'Free') }} </span>
          <span class="base-text bold-text">
            {{ workflowLabel(calculateTotalAvailableSeats()) }}/{{ workflowLabel(audienceCapacity) }}
          </span>
        </div>
        <div class="zoom-controls">
          <v-icon id="zoomIn" :size="43" :color="zoomInIconColor" style="user-select: none">
            add_circle
          </v-icon>
          <v-icon id="zoomOut" :size="43" :color="zoomOutIconColor" style="user-select: none">
            remove_circle
          </v-icon>
        </div>
      </div>
    </div>
    <seat-details-modal
      v-if="seatDetailsModalVisible"
      :visible="seatDetailsModalVisible"
      :loading="seatDetailsLoading"
      :details-type="activeDetailsType"
      :seat-details="selectedSeatData"
      :seat-details-list="selectedSeatDataByOrder"
      :parter-summary="selectedParterData"
      :event-id="selectedEventId || eventId"
      @close="closeSeatDetailsModal"
    />
  </v-layout>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import Spinner from '../shared/spinner.vue'
import SeatDetailsModal from './seat-details-modal.vue'
import {
  GET_EVENTS_BY_EVENT_TEMPLATE_ID,
  GET_FLOOR_PLAN_AVAILABILITY_OVERVIEW_DATA,
} from '@/graphql/event.graphql'
import moment from 'moment/moment'
import { getFloorPlanOverviewSeatDetailsByTicketLocation } from '@/http'
import { Venue } from '@/types'
import * as svgPanZoom from 'svg-pan-zoom'
import * as Hammer from 'hammerjs'

export default {
  mixins: [WorkflowDisplay],
  name: 'floor-plan-overview',
  components: {
    Spinner,
    SeatDetailsModal,
  },
  props: {
    eventId: String,
    eventTemplateId: String,
    organizationSlug: String,
  },
  data() {
    return {
      selectedEventId: null,
      performanceDates: [],
      showFloorPlan: false,
      venue: null,
      orderItems: [],
      availableSeats: [],
      disabledSeats: [],
      soldsSeats: [],
      zoomLevel: 1,
      lastPinchDistance: null,
      coloringInProgress: true,
      audienceCapacity: 0,
      seatDetailsModalVisible: false,
      seatDetailsLoading: false,
      activeDetailsType: null,
      selectedSeatData: {},
      selectedSeatDataByOrder: [],
      selectedParterData: null,
      selectedOrderItemIds: [],
      lastSelectedOrderItemIds: [],
      isMobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),
      zoomInIconColor: '#333',
      zoomOutIconColor: '#333',
      maxZoom: 3,
      startX: null,
      startY: null,
      multiTouch: false,
    }
  },
  computed: {
    floorPlanSvgStyle() {
      if (!this.isArenaFanPitVenue()) {
        return { width: '100%' }
      }

      return {
        width: '100%',
        height: 'min(72vh, 720px)',
        objectFit: 'contain',
      }
    },
    orderItemSelectItems() {
      return this.orderItems.map(orderItem => ({
        id: orderItem.id,
        label: `${orderItem.name}: ${orderItem.ticketsNumber}`,
        color: orderItem.color || '#A1A0A0',
      }))
    },
  },
  watch: {
    async selectedEventId(val) {
      if (!val) return
      this.showFloorPlan = false

      const { data } = await this.$apollo.query({
        query: GET_FLOOR_PLAN_AVAILABILITY_OVERVIEW_DATA,
        variables: { eventId: this.selectedEventId },
      })
      if (!data || !data.getFloorPlanAvailabilityOverviewData) {
        return (this.showFloorPlan = true)
      }
      const { venue, soldOrderItems, availableSeats, disabledSeats, audienceCapacity, soldsSeats } =
        data.getFloorPlanAvailabilityOverviewData
      this.venue = venue
      this.orderItems = soldOrderItems.map(item => {
        return {
          ...item,
          itemMeta: this.normalizeItemMeta(item.itemMeta),
          selected: false,
        }
      })
      this.selectedOrderItemIds = []
      this.lastSelectedOrderItemIds = []
      this.availableSeats = availableSeats
      this.disabledSeats = disabledSeats
      this.soldsSeats = soldsSeats
      this.audienceCapacity = audienceCapacity

      this.coloringInProgress = false
      this.showFloorPlan = true

      setTimeout(() => {
        const svgElement = document.getElementById('svgfile')
        if (svgElement) {
          svgElement.addEventListener('load', () => {
            this.hammerInit()
          })
          if (svgElement.contentDocument && svgElement.contentDocument.readyState === 'complete') {
            this.hammerInit()
          }
        }
      }, 0)
    },
    selectedOrderItemIds(newVal, oldVal) {
      this.syncSelectedOrderItems(newVal, oldVal)
    },
  },
  created() {
    if (this.eventTemplateId) {
      this.fetchPerformances()
    } else {
      this.selectedEventId = this.eventId
    }
  },
  methods: {
    isArenaFanPitVenue() {
      return (
        !!this.venue &&
        [
          Venue.ARENA_ZAGREB_STOLOVI_FAN_PIT,
          Venue.ARENA_ZAGREB_STOLOVI_FAN_PIT_BEZ_GORNJIH_SEKTORA,
        ].includes(this.venue.name)
      )
    },
    parseMetaBoolean(value) {
      if (value === true || value === 'true') return true
      if (value === false || value === 'false') return false

      return Boolean(value)
    },
    normalizeItemMeta(itemMeta) {
      if (!itemMeta || typeof itemMeta !== 'object') return itemMeta || {}

      return {
        ...itemMeta,
        parter: this.parseMetaBoolean(itemMeta.parter),
        table: this.parseMetaBoolean(itemMeta.table),
      }
    },
    resolveTicketLocation(event) {
      const svgDoc = document.getElementById('svgfile').contentDocument
      if (!svgDoc || !event || !event.target) return ''

      let currentElement = event.target
      while (currentElement && currentElement !== svgDoc) {
        if (currentElement.id) return currentElement.id
        currentElement = currentElement.parentNode
      }

      return ''
    },
    onSvgLoaded() {
      const svgDoc = document.getElementById('svgfile').contentDocument
      if (!svgDoc) return

      const allSeatIds = [
        ...this.availableSeats,
        ...this.disabledSeats,
        ...this.orderItems.flatMap(item => item.seats),
      ]
      const uniqueSeatIds = new Set(allSeatIds)

      for (const seatId of uniqueSeatIds) {
        const seatElement = svgDoc.getElementById(seatId)
        const isSold = this.soldsSeats.includes(seatId)
        const isParterSold = this.orderItems.some(
          item => this.parseMetaBoolean(item.itemMeta.parter) && item.seats.includes(seatId),
        )
        const isTableSold = this.orderItems.some(
          item => this.parseMetaBoolean(item.itemMeta.table) && item.seats.includes(seatId),
        )

        if (seatElement && (isSold || isParterSold || isTableSold)) {
          seatElement.style.cursor = 'pointer'
        }
      }

      svgDoc.addEventListener(this.isMobile ? 'touchstart' : 'mousedown', e => {
        if (this.isMobile) {
          this.multiTouch = e.touches.length > 1
          this.startY = e.touches[0].clientY
          this.startX = e.touches[0].clientX
        } else {
          this.startY = e.pageY
          this.startX = e.pageX
        }
      })

      svgDoc.addEventListener(this.isMobile ? 'touchend' : 'mouseup', event => {
        const ticketLocation = this.resolveTicketLocation(event)
        if (!ticketLocation) return

        const delta = 6
        const diffX = Math.abs(
          this.isMobile ? event.changedTouches[0].clientX - this.startX : event.pageX - this.startX,
        )
        const diffY = Math.abs(
          this.isMobile ? event.changedTouches[0].clientY - this.startY : event.pageY - this.startY,
        )
        if (!this.multiTouch && diffX < delta && diffY < delta) {
          const isSold = this.soldsSeats.includes(ticketLocation)
          const isParterSold = this.orderItems.some(
            item => this.parseMetaBoolean(item.itemMeta.parter) && item.seats.includes(ticketLocation),
          )
          const isTableSold = this.orderItems.some(
            item => this.parseMetaBoolean(item.itemMeta.table) && item.seats.includes(ticketLocation),
          )

          if (isSold || isParterSold || isTableSold) {
            this.handleSeatClick(ticketLocation, isParterSold)
          }
        }
      })

      this.changeSeatColors()
    },
    async handleSeatClick(seatId, isParter = false) {
      const clickedOrderItem = this.orderItems.find(item => item.seats.includes(seatId))
      const isTableTicket = this.parseMetaBoolean(clickedOrderItem.itemMeta.table)
      const isParterTicket = this.parseMetaBoolean(clickedOrderItem.itemMeta.parter)
      this.selectedParterData = null
      this.activeDetailsType = isParterTicket ? 'parter' : isTableTicket ? 'table' : 'seat'
      this.selectedSeatData = {
        data: {
          ticketLocation: seatId,
        },
        numberOfSeats: 0,
        orderItem: clickedOrderItem || { itemMeta: {}, name: '' },
      }
      this.seatDetailsLoading = true
      this.seatDetailsModalVisible = true

      let data = []
      try {
        data = await getFloorPlanOverviewSeatDetailsByTicketLocation(
          this.selectedEventId,
          seatId,
        )
      } catch (e) {
        this.closeSeatDetailsModal()

        return
      } finally {
        this.seatDetailsLoading = false
      }

      if (!data.length) {
        this.closeSeatDetailsModal()

        return
      }
      if (isParter) {
        const ticketMap = new Map()
        for (const accreditation of data) {
          const orderItemName =
            (accreditation.purchases && accreditation.purchases.orderItem && accreditation.purchases.orderItem.name) ||
            'workflowsAll.unknown_ticket_b4bcf61'
          ticketMap.set(orderItemName, (ticketMap.get(orderItemName) || 0) + 1)
        }

        const parterTickets = Array.from(ticketMap.entries())
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count)

        this.selectedParterData = {
          ticketLocation: seatId,
          totalSold: data.length,
          tickets: parterTickets,
        }
        this.selectedSeatData = {
          data: {
            ticketLocation: seatId,
            purchases: {
              orderItem: {
                name: parterTickets[0] ? parterTickets[0].name : 'Parter',
                itemMeta: { parter: true },
              },
            },
          },
          numberOfSeats: data.length,
          orderItem: {
            name: parterTickets[0] ? parterTickets[0].name : 'Parter',
            itemMeta: { parter: true },
          },
        }
        this.selectedSeatDataByOrder = []

        return
      }

      this.selectedSeatDataByOrder = this.groupSeatDetailsByOrder(data)
      this.selectedSeatData = this.selectedSeatDataByOrder[0]
    },
    groupSeatDetailsByOrder(accreditations) {
      const orders = new Map()

      for (const accreditation of accreditations) {
        const orderId = accreditation.purchases && accreditation.purchases.orderId
        // A purchase always has an order, but keep incomplete API records separate rather than
        // accidentally presenting multiple buyers as one order.
        const orderKey = orderId == null ? `accreditation-${accreditation.id}` : String(orderId)
        const existing = orders.get(orderKey)

        if (existing) {
          existing.numberOfSeats += 1
        } else {
          orders.set(orderKey, {
            data: accreditation,
            numberOfSeats: 1,
            orderItem: accreditation.purchases.orderItem,
          })
        }
      }

      return Array.from(orders.values())
    },
    closeSeatDetailsModal() {
      this.seatDetailsModalVisible = false
      this.seatDetailsLoading = false
      this.activeDetailsType = null
      this.selectedSeatData = {}
      this.selectedSeatDataByOrder = []
      this.selectedParterData = null
    },
    getCurrZoom() {
      return window.panZoom.getZoom()
    },
    checkZoomButtonsState(isZoomingIn) {
      if (isZoomingIn) {
        if (this.getCurrZoom() >= this.maxZoom - 0.1) {
          this.zoomInIconColor = '#D9D9D9'
        }
        if (this.getCurrZoom() > 0.51) {
          this.zoomOutIconColor = '#333'
        }
      } else {
        if (this.getCurrZoom() <= this.maxZoom - 0.1) {
          this.zoomInIconColor = '#333'
        }
        if (this.getCurrZoom() <= 0.51) {
          this.zoomOutIconColor = '#D9D9D9'
        }
      }
    },
    setZoomButtonsActions() {
      document.getElementById('zoomIn').addEventListener('click', () => {
        window.panZoom.zoomIn()
        this.checkZoomButtonsState(true)
      })
      document.getElementById('zoomOut').addEventListener('click', () => {
        window.panZoom.zoomOut()
        this.checkZoomButtonsState(false)
      })
    },
    hammerInit: function () {
      let eventsHandler
      eventsHandler = {
        haltEventListeners: ['touchstart', 'touchend', 'touchmove', 'touchleave', 'touchcancel'],
        init: function (options) {
          let instance = options.instance
          let initialScale = 1
          let pannedX = 0
          let pannedY = 0
          // Init Hammer
          // Listen only for pointer and touch events
          this.hammer = Hammer(options.svgElement, {
            inputClass: Hammer.SUPPORT_POINTER_EVENTS
              ? Hammer.PointerEventInput
              : Hammer.TouchInput,
          })
          // Enable pinch
          this.hammer.get('pinch').set({
            enable: true,
          })
          // Handle double tap
          this.hammer.on('doubletap', function () {
            instance.zoomIn()
          })
          // Handle pan
          this.hammer.on('panstart panmove', function (ev) {
            // On pan start reset panned variables
            if (ev.type === 'panstart') {
              pannedX = 0
              pannedY = 0
            }
            // Pan only the difference
            instance.panBy({
              x: ev.deltaX - pannedX,
              y: ev.deltaY - pannedY,
            })
            pannedX = ev.deltaX
            pannedY = ev.deltaY
          })
          // Handle pinch
          this.hammer.on('pinchstart pinchmove', function (ev) {
            // On pinch start remember initial zoom
            if (ev.type === 'pinchstart') {
              initialScale = instance.getZoom()
              instance.zoomAtPoint(initialScale * ev.scale, {
                x: ev.center.x,
                y: ev.center.y,
              })
            }
            instance.zoomAtPoint(initialScale * ev.scale, {
              x: ev.center.x,
              y: ev.center.y,
            })
          })
          // Prevent moving the page on some devices when panning over SVG
          options.svgElement.addEventListener('touchmove', function (e) {
            e.preventDefault()
          })
        },
        destroy: function () {
          this.hammer.destroy()
        },
      }
      let maxZoom = this.isMobile ? 5 : 3
      if (
        [
          'Arena Zagreb',
          'Bundek - Baš Naš',
          'Arena Zagreb 2',
          Venue.ARENA_ZAGREB_PARTER_EXTENDED,
          Venue.ARENA_ZAGREB_PARTER_A_B,
          Venue.KC_DRAZEN_PETROVIC,
        ].includes(this.venue.name)
      ) {
        if (this.isMobile) {
          maxZoom = 35
        } else {
          maxZoom = 7
        }
      }
      // Expose to window namespace for testing purposes
      window.panZoom = svgPanZoom('#svgfile', {
        zoomEnabled: true,
        controlIconsEnabled: false,
        maxZoom,
        fit: 2,
        center: 1,
        customEventsHandler: eventsHandler,
      })
      this.setZoomButtonsActions()
    },
    changeSeatColors() {
      const svgDoc = document.getElementById('svgfile').contentDocument
      if (!svgDoc) return

      const allVenueSeats = new Set((this.venue && this.venue.seats) || [])
      for (const seatId of allVenueSeats) {
        const seatElement = svgDoc.getElementById(seatId)
        if (seatElement) {
          this.applyColorToSeatElement(seatElement, { fill: '#D9D9D9' })
        }
      }

      for (const availableSeatId of this.availableSeats) {
        const seatElement = svgDoc.getElementById(availableSeatId)

        if (seatElement) {
          this.applyColorToSeatElement(seatElement, { fill: '#979797' })
        }
      }

      for (const orderItem of this.orderItems) {
        const soldSeatsIds = orderItem.seats
        const isParter = this.parseMetaBoolean(orderItem.itemMeta.parter)
        const isTable = this.parseMetaBoolean(orderItem.itemMeta.table)

        for (const seatId of soldSeatsIds) {
          const seatElement = svgDoc.getElementById(seatId)

          if (seatElement) {
            if (!isParter && !isTable && this.availableSeats.includes(seatId)) {
              this.applyColorToSeatElement(seatElement, {
                stroke: orderItem.selected ? orderItem.color : '#F5F5F5',
                strokeWidth: 5,
              })
            } else {
              this.applyColorToSeatElement(seatElement, {
                fill: orderItem.selected ? orderItem.color : '#F5F5F5',
                stroke: isParter ? '#F5F5F5' : undefined,
              })
            }
          }
        }
      }

      for (const disabledSeatId of this.disabledSeats) {
        const seatElement = svgDoc.getElementById(disabledSeatId)

        if (seatElement) {
          this.applyColorToSeatElement(seatElement, { fill: '#D9D9D9' })
        }
      }

      this.coloringInProgress = false
    },
    applyColorToSeatElement(element, { fill, stroke, strokeWidth }) {
      const paintableChildren = element.querySelectorAll('path, circle, ellipse, rect, polygon, polyline, line')
      const elementsToPaint = paintableChildren.length ? [element, ...paintableChildren] : [element]
      for (const target of elementsToPaint) {
        if (fill) {
          target.style.fill = fill
        }
        if (stroke) {
          target.style.stroke = stroke
        }
        if (typeof strokeWidth === 'number') {
          target.style.strokeWidth = strokeWidth
        }
      }
    },
    updateOrderItemSeatColors(orderItem, svgDoc) {
      if (!orderItem || !svgDoc) return

      const isParter = this.parseMetaBoolean(orderItem.itemMeta.parter)
      const isTable = this.parseMetaBoolean(orderItem.itemMeta.table)
      const seatColor = orderItem.selected ? (orderItem.color || '#A1A0A0') : '#F5F5F5'

      for (const seatId of orderItem.seats || []) {
        if (this.disabledSeats.includes(seatId)) continue
        const seatElement = svgDoc.getElementById(seatId)
        if (!seatElement) continue
        if (!isParter && !isTable && this.availableSeats.includes(seatId)) {
          this.applyColorToSeatElement(seatElement, { stroke: seatColor, strokeWidth: 5 })
        } else {
          this.applyColorToSeatElement(seatElement, { fill: seatColor, stroke: isParter ? '#F5F5F5' : undefined })
        }
      }
    },
    syncSelectedOrderItems(newSelected = this.selectedOrderItemIds, oldSelected = this.lastSelectedOrderItemIds) {
      const svgObject = document.getElementById('svgfile')
      const svgDoc = svgObject ? svgObject.contentDocument : null

      const next = new Set((newSelected || []).map(id => String(id)))
      const prev = new Set((oldSelected || []).map(id => String(id)))

      const changedIds = new Set()
      for (const id of next) if (!prev.has(id)) changedIds.add(id)
      for (const id of prev) if (!next.has(id)) changedIds.add(id)

      for (const orderItem of this.orderItems) {
        const id = String(orderItem.id)
        const nextSelected = next.has(id)
        const wasSelected = prev.has(id)
        orderItem.selected = nextSelected

        if (svgDoc && changedIds.has(id) && nextSelected !== wasSelected) {
          this.updateOrderItemSeatColors(orderItem, svgDoc)
        }
      }

      this.lastSelectedOrderItemIds = Array.isArray(newSelected) ? [...newSelected] : []
    },
    resolveOrderItemId(selectionItem) {
      if (selectionItem == null) return null
      if (typeof selectionItem === 'number' || typeof selectionItem === 'string') {
        return selectionItem
      }

      return selectionItem.id || selectionItem.value || null
    },
    removeSelectedOrderItemBySelection(selectionItem) {
      let orderItemId = this.resolveOrderItemId(selectionItem)
      if (orderItemId == null && selectionItem && selectionItem.label) {
        const matchedOrderItem = this.orderItemSelectItems.find(item => item.label === selectionItem.label)
        orderItemId = matchedOrderItem ? matchedOrderItem.id : null
      }
      if (orderItemId == null) return

      const normalizedOrderItemId = String(orderItemId)
      this.selectedOrderItemIds = this.selectedOrderItemIds.filter(id => String(id) !== normalizedOrderItemId)
    },
    calculateTotalAvailableSeats() {
      return (
        this.audienceCapacity - this.orderItems.reduce((acc, curr) => acc + curr.ticketsNumber, 0)
      )
    },
    async fetchPerformances() {
      const {
        data: { getEventsByEventTemplateId },
      } = await this.$apollo.query({
        query: GET_EVENTS_BY_EVENT_TEMPLATE_ID,
        variables: {
          organizationSlug: this.organizationSlug,
          eventTemplateId: this.eventTemplateId,
        },
      })

      // Normalize and sort by performance date
      const normalized = getEventsByEventTemplateId
        .map(p => ({ ...p, fromFormatted: moment(p.from).format('DD.MM.YYYY u HH:mm') }))
        .sort((a, b) => new Date(a.from).getTime() - new Date(b.from).getTime())

      this.performanceDates = normalized.map(p => ({ ...p, from: p.fromFormatted }))

      // Prefer the first event that actually has a floor plan (venueId present)
      const withFloorPlan = normalized.find(p => !!p.venueId)
      const first = withFloorPlan || normalized[0]
      this.selectedEventId = first ? first.id : ''
    },
  },
}
</script>

<style scoped>
.wrapper {
  max-width: 1033px;
}

@media screen and (max-width: 800px) {
  .wrapper {
    margin-top: 1.5rem;
  }
}

.floor-plan-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 0.75rem;
  border: 1px solid #d9d9d9;
}

.order-items-container {
  display: flex;
  flex-direction: column;
  padding: 1rem 1.5rem;
  align-items: flex-start;
  gap: 1rem;
  align-self: stretch;
  border-bottom: 1px solid #d9d9d9;
  overflow: hidden;
}

.order-items-dropdown {
  width: 100%;
  max-width: 640px;
}

.order-item-select-option,
.order-item-select-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.order-item-select-chip {
  margin-right: 0.5rem;
  margin-bottom: 0.35rem;
  border: 1px solid #dbe7ff;
  background: linear-gradient(180deg, #ffffff 0%, #f4f8ff 100%) !important;
  border-radius: 999px !important;
  box-shadow: 0 1px 3px rgba(2, 119, 189, 0.12);
  color: #1f2937 !important;
}

.order-item-select-chip:hover {
  border-color: #b6cdfa;
  box-shadow: 0 2px 6px rgba(2, 119, 189, 0.18);
}

.order-item-select-color {
  box-shadow: 0 0 0 2px #fff, 0 0 0 3px rgba(2, 119, 189, 0.22);
}

.order-item-select-label {
  font-size: 0.825rem;
  font-weight: 600;
  letter-spacing: 0.01em;
}

.order-item-select-remove {
  margin-left: 0.125rem;
  width: 1.125rem;
  height: 1.125rem;
  border: 0;
  border-radius: 999px;
  background: rgba(75, 85, 99, 0.1);
  color: #4b5563;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.order-item-select-remove:hover {
  background: rgba(75, 85, 99, 0.2);
}

.color {
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 10000px;
}

.base-text {
  color: #666;
  font-family: 'Source Sans Pro';
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}

.bold-text {
  font-weight: 700;
}

.floor-plan-container {
  padding: 3rem;
  position: relative;
  overflow: hidden;
  width: 100%;
}

.object-container {
  overflow: scroll;

  -ms-overflow-style: none;
  scrollbar-width: none;
}

.object-container::-webkit-scrollbar {
  display: none;
}

.total-label {
  position: absolute;
  left: 1.5rem;
  bottom: 1.5rem;
}

.zoom-controls {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1rem;
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 1;
}
</style>
